/**
 * Sicheres Markdown für Nachrichten (CommonMark/GFM + Discord-Erweiterungen, Vertrag 3.1):
 * `||Spoiler||`, `__unterstrichen__`, `~~durch~~`, `> Zitat`, `>>> Block-Zitat`, Codeblöcke, `-# Kleintext`,
 * `<@id>`, `<@&id>`, `<#id>`, `<:name:id>`, `<a:name:id>`, `<t:unix:F>`, `@everyone`, `@here`.
 *
 * Sicherheit: Roh-HTML wird nie übernommen (escaped), Links nur http(s)/mailto, Bilder nur eigene Emojis
 * (`/api/media/`), das Ergebnis läuft zusätzlich durch DOMPurify mit enger Allowlist.
 */
import DOMPurify from 'dompurify';
import { Marked, type Tokens, type TokenizerAndRendererExtension } from 'marked';

export interface MarkdownContext {
  resolveUser?(id: string): { name: string } | null;
  resolveRole?(id: string): { name: string; color: string | null } | null;
  resolveChannel?(id: string): { name: string; type?: string } | null;
  resolveEmoji?(id: string): { url: string } | null;
  /** Eigene ID / Rollen → Erwähnungen hervorheben. */
  meId?: string | null;
  myRoleIds?: readonly string[];
  /** Animierte Emojis abspielen (sonst statisch über ?animated=0 – Server liefert immer Original; nur Klasse). */
  animateEmojis?: boolean;
}

export interface RenderOptions {
  /** Nur Inline-Elemente (Antwort-Vorschau, Kanalthema, Vorschauen). */
  inline?: boolean;
}

const esc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const SAFE_HREF = /^(https?:\/\/|mailto:)/i;

/** Linktext, der selbst wie eine Adresse aussieht (`https://…`, `www.…`, `bank.de/login`). */
const URL_LIKE = /^(?:[a-z][a-z0-9+.-]*:\/\/)?(?:[\w-]+\.)+[a-z]{2,}(?::\d+)?(?:[/?#]\S*)?$/i;

function hostOf(raw: string): string | null {
  try {
    const u = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`);
    return u.hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return null;
  }
}

/**
 * Phishing-Schutz für maskierte Links: zeigt der Linktext eine Adresse, die auf einen anderen Host verweist
 * als das Ziel (`[https://bank.de](https://boese.example)`), ist er irreführend.
 */
export function isMisleadingLinkText(text: string, href: string): boolean {
  // Formatierung (`**…**`, `~~…~~`, Spoiler) zählt nicht – sie täuscht ebenso.
  const t = text.replace(/[*_~`|]/g, '').trim();
  if (!URL_LIKE.test(t)) return false;
  const shown = hostOf(t);
  const real = hostOf(href);
  return !!shown && shown !== real;
}

const TIMESTAMP_STYLES: Record<string, Intl.DateTimeFormatOptions> = {
  t: { timeStyle: 'short' },
  T: { timeStyle: 'medium' },
  d: { dateStyle: 'short' },
  D: { dateStyle: 'long' },
  f: { dateStyle: 'long', timeStyle: 'short' },
  F: { dateStyle: 'full', timeStyle: 'short' },
};

export function formatRelative(date: Date, now = Date.now()): string {
  const diff = (date.getTime() - now) / 1000;
  const rtf = new Intl.RelativeTimeFormat('de', { numeric: 'auto' });
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(Math.round(diff), 'second');
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
  if (abs < 86400 * 30) return rtf.format(Math.round(diff / 86400), 'day');
  if (abs < 86400 * 365) return rtf.format(Math.round(diff / (86400 * 30)), 'month');
  return rtf.format(Math.round(diff / (86400 * 365)), 'year');
}

export function formatTimestampTag(unix: number, style = 'f'): string {
  const date = new Date(unix * 1000);
  if (Number.isNaN(date.getTime())) return String(unix);
  if (style === 'R') return formatRelative(date);
  return new Intl.DateTimeFormat('de-DE', TIMESTAMP_STYLES[style] ?? TIMESTAMP_STYLES.f).format(date);
}

/** Erzeugt eine Marked-Instanz mit den Lagerfeuer-Erweiterungen für einen Kontext. */
function createMarked(ctx: MarkdownContext): Marked {
  const me = ctx.meId ?? null;
  const myRoles = new Set(ctx.myRoleIds ?? []);

  const ext: TokenizerAndRendererExtension[] = [
    {
      name: 'lfUserMention',
      level: 'inline',
      start: (src) => src.indexOf('<@'),
      tokenizer(src) {
        const m = /^<@!?(\d{1,20})>/.exec(src);
        if (m) return { type: 'lfUserMention', raw: m[0], id: m[1] };
        return undefined;
      },
      renderer(t) {
        const id = String(t.id);
        const u = ctx.resolveUser?.(id);
        const cls = id === me ? 'mention mention-me' : 'mention';
        return `<span class="${cls}" data-user-id="${esc(id)}" data-testid="mention-user" role="button" tabindex="0">@${esc(u?.name ?? 'Unbekannt')}</span>`;
      },
    },
    {
      name: 'lfRoleMention',
      level: 'inline',
      start: (src) => src.indexOf('<@&'),
      tokenizer(src) {
        const m = /^<@&(\d{1,20})>/.exec(src);
        if (m) return { type: 'lfRoleMention', raw: m[0], id: m[1] };
        return undefined;
      },
      renderer(t) {
        const id = String(t.id);
        const r = ctx.resolveRole?.(id);
        const color = r?.color && /^#[0-9a-f]{6}$/i.test(r.color) ? ` style="--lf-role-color:${r.color}"` : '';
        const cls = myRoles.has(id) ? 'mention role-mention mention-me' : 'mention role-mention';
        return `<span class="${cls}" data-role-id="${esc(id)}" data-testid="mention-role"${color}>@${esc(r?.name ?? 'Unbekannte Rolle')}</span>`;
      },
    },
    {
      name: 'lfChannelMention',
      level: 'inline',
      start: (src) => src.indexOf('<#'),
      tokenizer(src) {
        const m = /^<#(\d{1,20})>/.exec(src);
        if (m) return { type: 'lfChannelMention', raw: m[0], id: m[1] };
        return undefined;
      },
      renderer(t) {
        const id = String(t.id);
        const c = ctx.resolveChannel?.(id);
        return `<span class="mention channel-mention" data-channel-id="${esc(id)}" data-testid="mention-channel" role="link" tabindex="0">#${esc(c?.name ?? 'unbekannt')}</span>`;
      },
    },
    {
      name: 'lfEmoji',
      level: 'inline',
      start: (src) => {
        const m = /<a?:[A-Za-z0-9_]{2,32}:\d/.exec(src);
        return m ? m.index : undefined;
      },
      tokenizer(src) {
        const m = /^<(a?):([A-Za-z0-9_]{2,32}):(\d{1,20})>/.exec(src);
        if (m) return { type: 'lfEmoji', raw: m[0], animated: m[1] === 'a', name: m[2], id: m[3] };
        return undefined;
      },
      renderer(t) {
        const id = String(t.id);
        const name = String(t.name);
        const e = ctx.resolveEmoji?.(id);
        if (!e || !e.url.startsWith('/api/media/')) {
          return `<span class="emoji-unresolved" data-emoji-id="${esc(id)}" data-emoji-name="${esc(name)}">:${esc(name)}:</span>`;
        }
        const anim = t.animated ? ' emoji-animated' : '';
        return `<img class="emoji${anim}" src="${esc(e.url)}" alt=":${esc(name)}:" title=":${esc(name)}:" data-emoji-id="${esc(id)}" data-testid="custom-emoji" draggable="false">`;
      },
    },
    {
      name: 'lfTimestamp',
      level: 'inline',
      start: (src) => src.indexOf('<t:'),
      tokenizer(src) {
        const m = /^<t:(-?\d{1,13})(?::([tTdDfFR]))?>/.exec(src);
        if (m) return { type: 'lfTimestamp', raw: m[0], unix: Number(m[1]), style: m[2] ?? 'f' };
        return undefined;
      },
      renderer(t) {
        const unix = Number(t.unix);
        const d = new Date(unix * 1000);
        const iso = Number.isNaN(d.getTime()) ? '' : d.toISOString();
        const full = Number.isNaN(d.getTime()) ? '' : formatTimestampTag(unix, 'F');
        return `<time class="md-timestamp" datetime="${esc(iso)}" title="${esc(full)}">${esc(formatTimestampTag(unix, String(t.style)))}</time>`;
      },
    },
    {
      name: 'lfEveryone',
      level: 'inline',
      start: (src) => {
        const m = /@(everyone|here)\b/.exec(src);
        return m ? m.index : undefined;
      },
      tokenizer(src) {
        const m = /^@(everyone|here)\b/.exec(src);
        if (m) return { type: 'lfEveryone', raw: m[0], which: m[1] };
        return undefined;
      },
      renderer(t) {
        return `<span class="mention mention-me mention-everyone" data-testid="mention-everyone">@${esc(String(t.which))}</span>`;
      },
    },
    {
      name: 'lfSpoiler',
      level: 'inline',
      start: (src) => src.indexOf('||'),
      tokenizer(src) {
        const m = /^\|\|([\s\S]+?)\|\|/.exec(src);
        if (m) {
          const token = { type: 'lfSpoiler', raw: m[0], tokens: [] as Tokens.Generic[] };
          this.lexer.inline(m[1]!, token.tokens);
          return token;
        }
        return undefined;
      },
      renderer(t) {
        return `<span class="md-spoiler" data-spoiler="hidden" data-testid="spoiler" role="button" tabindex="0" aria-label="Spoiler anzeigen">${this.parser.parseInline(t.tokens ?? [])}</span>`;
      },
    },
    {
      name: 'lfUnderline',
      level: 'inline',
      start: (src) => src.indexOf('__'),
      tokenizer(src) {
        const m = /^__(?=\S)([\s\S]*?\S)__(?!_)/.exec(src);
        if (m) {
          const token = { type: 'lfUnderline', raw: m[0], tokens: [] as Tokens.Generic[] };
          this.lexer.inline(m[1]!, token.tokens);
          return token;
        }
        return undefined;
      },
      renderer(t) {
        return `<u>${this.parser.parseInline(t.tokens ?? [])}</u>`;
      },
    },
    {
      name: 'lfSubtext',
      level: 'block',
      start: (src) => {
        const m = /(^|\n)-# /.exec(src);
        return m ? m.index + m[1]!.length : undefined;
      },
      tokenizer(src) {
        const m = /^-# ([^\n]+)(?:\n|$)/.exec(src);
        if (m) {
          const token = { type: 'lfSubtext', raw: m[0], tokens: [] as Tokens.Generic[] };
          this.lexer.inline(m[1]!, token.tokens);
          return token;
        }
        return undefined;
      },
      renderer(t) {
        return `<small class="md-subtext">${this.parser.parseInline(t.tokens ?? [])}</small>\n`;
      },
    },
  ];

  const marked = new Marked({ gfm: true, breaks: true, async: false });
  marked.use({
    extensions: ext,
    tokenizer: {
      // Discord kennt keine Tabellen, Setext-Überschriften oder eingerückten Code – als Absatz behandeln.
      table: () => undefined,
      lheading: () => undefined,
      // Eingerückter Code (Fences laufen über `fences` und bleiben erhalten).
      code: () => undefined,
      // Roh-HTML nie als HTML erkennen.
      html: () => undefined,
      tag: () => undefined,
    },
    renderer: {
      heading({ tokens, depth }) {
        const level = Math.min(depth, 3);
        return `<h${level} class="md-h${level}">${this.parser.parseInline(tokens)}</h${level}>\n`;
      },
      link({ href, title, tokens, text: rawText }) {
        const text = this.parser.parseInline(tokens);
        if (!href || !SAFE_HREF.test(href)) return text;
        const rel = 'target="_blank" rel="noopener noreferrer nofollow ugc"';
        // Irreführender Linktext → als Klartext, dahinter das echte Ziel sichtbar als Link.
        if (isMisleadingLinkText(rawText, href)) {
          return `${esc(rawText)} (<a href="${esc(href)}" ${rel}>${esc(href)}</a>)`;
        }
        // Maskierte Links verraten ihr Ziel zumindest im Tooltip.
        const t = ` title="${esc(title || (rawText !== href ? href : ''))}"`;
        return `<a href="${esc(href)}"${title || rawText !== href ? t : ''} ${rel}>${text}</a>`;
      },
      image({ href, text }) {
        // Keine externen Bilder im Fließtext (Privatsphäre) – als Link darstellen.
        if (href && SAFE_HREF.test(href)) return `<a href="${esc(href)}" target="_blank" rel="noopener noreferrer nofollow ugc">${esc(text || href)}</a>`;
        return esc(text);
      },
      html({ text }) {
        return esc(text);
      },
      code({ text, lang }) {
        const l = lang && /^[\w+#.-]{1,24}$/.test(lang) ? ` class="language-${esc(lang)}"` : '';
        return `<pre class="md-codeblock"><code${l}>${esc(text.replace(/\n$/, ''))}</code></pre>\n`;
      },
      checkbox({ checked }) {
        return checked ? '☑ ' : '☐ ';
      },
    },
  });
  return marked;
}

/** `>>> ` (Rest der Nachricht als Zitat) in normale Zitatzeilen umschreiben. */
export function preprocess(src: string): string {
  const idx = src.search(/(^|\n)>>> /);
  if (idx < 0) return src;
  const start = src[idx] === '\n' ? idx + 1 : idx;
  const head = src.slice(0, start);
  const tail = src.slice(start + 4);
  return head + tail.split('\n').map((l) => `> ${l}`).join('\n');
}

// URLs (href/src) werden in den Hooks streng geprüft; ALLOWED_URI_REGEXP bliebe sonst auch für
// gewöhnliche Attributwerte (datetime, data-*) aktiv.
const ALLOWED_TAGS = ['p', 'br', 'strong', 'em', 'u', 'del', 's', 'code', 'pre', 'blockquote', 'ul', 'ol', 'li', 'a', 'span', 'img', 'time', 'h1', 'h2', 'h3', 'small', 'hr'];
const ALLOWED_ATTR = [
  'href', 'target', 'rel', 'title', 'class', 'src', 'alt', 'datetime', 'start', 'role', 'tabindex', 'aria-label', 'draggable',
  'data-user-id', 'data-role-id', 'data-channel-id', 'data-emoji-id', 'data-emoji-name', 'data-spoiler', 'style',
  // Nur feste Werte (siehe SAFE_TESTIDS), Roh-HTML wird ohnehin nie übernommen.
  'data-testid',
];

/** Test-IDs, die die Renderer selbst setzen (Playwright); alles andere wird entfernt. */
const SAFE_TESTIDS = /^(mention-(user|role|channel|everyone)|spoiler|custom-emoji)$/;

let hooksInstalled = false;
function installHooks(): void {
  if (hooksInstalled) return;
  hooksInstalled = true;
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName === 'style') {
      // Nur die Rollenfarbe einer Rollen-Erwähnung.
      data.keepAttr = node.nodeName === 'SPAN' && /^--lf-role-color:#[0-9a-fA-F]{6}$/.test(data.attrValue);
    }
    if (data.attrName === 'src') {
      data.keepAttr = node.nodeName === 'IMG' && data.attrValue.startsWith('/api/media/') && !data.attrValue.includes('..');
    }
    if (data.attrName === 'href') {
      data.keepAttr = node.nodeName === 'A' && SAFE_HREF.test(data.attrValue);
    }
    if (data.attrName === 'data-testid') {
      data.keepAttr = SAFE_TESTIDS.test(data.attrValue);
    }
    if (data.attrName === 'data-spoiler') {
      data.keepAttr = data.attrValue === 'hidden';
    }
    if (data.attrName === 'class') {
      data.attrValue = data.attrValue
        .split(/\s+/)
        .filter((c) => /^(md-[\w-]+|mention[\w-]*|role-mention|channel-mention|emoji[\w-]*|language-[\w+#.-]+)$/.test(c))
        .join(' ');
    }
  });
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.nodeName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer nofollow ugc');
    }
    if (node.nodeName === 'IMG' && !node.getAttribute('src')) node.remove();
  });
}

export function sanitizeHtml(html: string): string {
  installHooks();
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  });
}

/** Rendert Nachrichten-Markdown zu bereinigtem HTML. */
export function renderMarkdown(content: string, ctx: MarkdownContext = {}, opts: RenderOptions = {}): string {
  if (!content) return '';
  const marked = createMarked(ctx);
  const src = preprocess(content);
  const html = opts.inline ? (marked.parseInline(src.replace(/\n+/g, ' ')) as string) : (marked.parse(src) as string);
  return sanitizeHtml(html);
}

const EMOJI_ONLY = /^(?:\s|<a?:[A-Za-z0-9_]{2,32}:\d{1,20}>|\p{Extended_Pictographic}|\p{Emoji_Modifier}|\p{Emoji_Component}|‍|️)+$/u;

/** Nachricht besteht nur aus Emojis (≤ 27) → große Darstellung. */
export function isEmojiOnly(content: string): boolean {
  const t = content.trim();
  if (!t || !EMOJI_ONLY.test(t) || /^[\d#*\s]+$/.test(t)) return false;
  const custom = t.match(/<a?:[A-Za-z0-9_]{2,32}:\d{1,20}>/g)?.length ?? 0;
  const rest = t.replace(/<a?:[A-Za-z0-9_]{2,32}:\d{1,20}>/g, '');
  const uni = rest.match(/\p{Extended_Pictographic}/gu)?.length ?? 0;
  return custom + uni > 0 && custom + uni <= 27;
}
